import { Routes, Route } from 'react-router-dom';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import Sidebar from './components/layout/Sidebar';
import { useAuth } from './hooks/useAuth';
import { LanguageProvider } from './lib/LanguageContext';
import { useState, useEffect } from 'react';

const HomePage = () => import('./pages/HomePage');
const MovieDetailPage = () => import('./pages/MovieDetailPage');
const TVDetailPage = () => import('./pages/TVDetailPage');
const SeasonDetailPage = () => import('./pages/SeasonDetailPage');
const PersonDetailPage = () => import('./pages/PersonDetailPage');
const GenrePage = () => import('./pages/GenrePage');
const CollectionPage = () => import('./pages/CollectionPage');
const CompanyPage = () => import('./pages/CompanyPage');
const SearchPage = () => import('./pages/SearchPage');
const ArticlesPage = () => import('./pages/ArticlesPage');
const ArticleDetailPage = () => import('./pages/ArticleDetailPage');
const FavoritesPage = () => import('./pages/FavoritesPage');
const WatchlistPage = () => import('./pages/WatchlistPage');
const HistoryPage = () => import('./pages/HistoryPage');
const LoginPage = () => import('./pages/LoginPage');
const RegisterPage = () => import('./pages/RegisterPage');
const AdminLayout = () => import('./pages/admin/AdminLayout');
const AdminDashboard = () => import('./pages/admin/AdminDashboard');
const AdminArticles = () => import('./pages/admin/AdminArticles');
const AdminPages = () => import('./pages/admin/AdminPages');
const AdminUsers = () => import('./pages/admin/AdminUsers');
const AdminSettings = () => import('./pages/admin/AdminSettings');
const AdminAds = () => import('./pages/admin/AdminAds');
const MoviesPage = () => import('./pages/MoviesPage');
const TVShowsPage = () => import('./pages/TVShowsPage');
const CalendarPage = () => import('./pages/CalendarPage');
const AdminLogs = () => import('./pages/admin/AdminLogs');
const NotFoundPage = () => import('./pages/NotFoundPage');

import React, { Suspense } from 'react';
import Skeleton from './components/common/Skeleton';
import AdminGate from './components/admin/AdminGate';
import AdBanner, { loadAnalyticsScripts } from './components/common/AdBanner';

function LazyPage(factory: () => Promise<{ default: React.ComponentType }>) {
  const Component = React.lazy(factory);
  return (
    <Suspense fallback={<div className="min-h-screen pt-20 p-4"><Skeleton.Hero /></div>}>
      <Component />
    </Suspense>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen pt-20 p-4"><Skeleton.Hero /></div>;
  if (!user) {
    return <React.Suspense fallback={<Skeleton.Hero />}>{LazyPage(LoginPage)}</React.Suspense>;
  }
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  return (
    <AdminGate>
      {children}
    </AdminGate>
  );
}

export default function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    loadAnalyticsScripts();
  }, []);

  return (
    <LanguageProvider>
    <div className="min-h-screen flex flex-col">
      <AdBanner placement="popunder" />
      <Navbar onMenuToggle={() => setSidebarOpen(true)} />
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <main className="flex-1 pt-16">
        <Routes>
          <Route path="/" element={LazyPage(HomePage)} />
          <Route path="/movies/:category" element={LazyPage(MoviesPage)} />
          <Route path="/tvshows/:category" element={LazyPage(TVShowsPage)} />
          <Route path="/calendar" element={LazyPage(CalendarPage)} />
          <Route path="/movie/:id" element={LazyPage(MovieDetailPage)} />
          <Route path="/movie/:id/:slug" element={LazyPage(MovieDetailPage)} />
          <Route path="/tv/:id" element={LazyPage(TVDetailPage)} />
          <Route path="/tv/:id/:slug" element={LazyPage(TVDetailPage)} />
          <Route path="/tv/:id/season/:season" element={LazyPage(SeasonDetailPage)} />
          <Route path="/person/:id" element={LazyPage(PersonDetailPage)} />
          <Route path="/genre/:type/:slug" element={LazyPage(GenrePage)} />
          <Route path="/collection/:id" element={LazyPage(CollectionPage)} />
          <Route path="/company/:id" element={LazyPage(CompanyPage)} />
          <Route path="/search" element={LazyPage(SearchPage)} />
          <Route path="/articles" element={LazyPage(ArticlesPage)} />
          <Route path="/articles/:slug" element={LazyPage(ArticleDetailPage)} />
          <Route path="/login" element={LazyPage(LoginPage)} />
          <Route path="/register" element={LazyPage(RegisterPage)} />
          <Route
            path="/favorites"
            element={<ProtectedRoute>{LazyPage(FavoritesPage)}</ProtectedRoute>}
          />
          <Route
            path="/watchlist"
            element={<ProtectedRoute>{LazyPage(WatchlistPage)}</ProtectedRoute>}
          />
          <Route
            path="/history"
            element={<ProtectedRoute>{LazyPage(HistoryPage)}</ProtectedRoute>}
          />
          <Route
            path="/admin"
            element={<AdminRoute>{LazyPage(AdminLayout)}</AdminRoute>}
          >
            <Route index element={LazyPage(AdminDashboard)} />
            <Route path="articles" element={LazyPage(AdminArticles)} />
            <Route path="pages" element={LazyPage(AdminPages)} />
            <Route path="ads" element={LazyPage(AdminAds)} />
            <Route path="users" element={LazyPage(AdminUsers)} />
            <Route path="settings" element={LazyPage(AdminSettings)} />
            <Route path="logs" element={LazyPage(AdminLogs)} />
          </Route>
          <Route path="*" element={LazyPage(NotFoundPage)} />
        </Routes>
      </main>
      <Footer />
    </div>
    </LanguageProvider>
  );
}
